// ============================================================================
// KLINISOL CRO - Personnel Module API Service
// File: src/api/personnelApi.js
// Version: 1.0
// Date: December 3, 2025
// ============================================================================

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://klinisol.com/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const personnelApi = {
    // ========================================================================
    // DIRECTORY & OVERVIEW
    // ========================================================================

    /**
     * Get personnel directory
     * @returns {Promise} - List of all staff with qualification summary
     */
    getDirectory: async () => {
        const response = await api.get('/personnel');
        return response.data;
    },

    /**
     * Get compliance summary report
     * @returns {Promise} - Compliance summary for all staff
     */
    getComplianceSummary: async () => {
        const response = await api.get('/personnel/compliance-summary');
        return response.data;
    },

    /**
     * Get expiring credentials report
     * @param {number} days - Days ahead to check (default 90)
     * @returns {Promise} - List of expiring credentials
     */
    getExpiringCredentials: async (days = 90) => {
        const response = await api.get(`/personnel/expiring-credentials?days=${days}`);
        return response.data;
    },

    /**
     * Get personnel access audit log
     * @param {Object} filters - Filter options (userId, days, action)
     * @returns {Promise} - Access log entries
     */
    getAccessLog: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.userId) params.append('userId', filters.userId);
        if (filters.days) params.append('days', filters.days);
        if (filters.action) params.append('action', filters.action);
        const response = await api.get(`/personnel/access-log?${params.toString()}`);
        return response.data;
    },

    // ========================================================================
    // PROFILE
    // ========================================================================

    /**
     * Get staff profile
     * @param {number} userId - User ID
     * @returns {Promise} - Full profile data
     */
    getProfile: async (userId) => {
        const response = await api.get(`/personnel/${userId}`);
        return response.data;
    },

    /**
     * Update qualification record
     * @param {number} userId - User ID
     * @param {Object} data - Qualification data
     * @returns {Promise} - Update result
     */
    updateQualifications: async (userId, data) => {
        const response = await api.put(`/personnel/${userId}/qualifications`, data);
        return response.data;
    },

    // ========================================================================
    // DOCUMENTS
    // ========================================================================

    /**
     * Get user's documents
     * @param {number} userId - User ID
     * @returns {Promise} - List of documents
     */
    getUserDocuments: async (userId) => {
        const response = await api.get(`/personnel/${userId}/documents`);
        return response.data;
    },

    /**
     * Upload document
     * @param {number} userId - User ID
     * @param {FormData} formData - Form data with file and metadata
     * @returns {Promise} - Upload result
     */
    uploadDocument: async (userId, formData) => {
        const response = await api.post(`/personnel/${userId}/documents`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    /**
     * Approve or reject document
     * @param {number} documentId - Document ID
     * @param {string} status - 'approved' or 'rejected'
     * @param {string} reason - Rejection reason (if rejecting)
     * @returns {Promise} - Approval result
     */
    approveDocument: async (documentId, status, reason = null) => {
        const response = await api.put(`/personnel/documents/${documentId}/approve`, {
            approval_status: status,
            rejection_reason: reason
        });
        return response.data;
    },

    /**
     * Download document
     * @param {number} documentId - Document ID
     * @returns {Promise} - File blob
     */
    downloadDocument: async (documentId) => {
        const response = await api.get(`/personnel/documents/${documentId}/download`, {
            responseType: 'blob'
        });
        return response.data;
    },

    // ========================================================================
    // CREDENTIALS
    // ========================================================================

    /**
     * Add credential
     * @param {number} userId - User ID
     * @param {Object} data - Credential data
     * @returns {Promise} - Add result
     */
    addCredential: async (userId, data) => {
        const response = await api.post(`/personnel/${userId}/credentials`, data);
        return response.data;
    },

    /**
     * Verify credential
     * @param {number} credentialId - Credential ID
     * @param {Object} data - Verification data
     * @returns {Promise} - Verification result
     */
    verifyCredential: async (credentialId, data) => {
        const response = await api.put(`/personnel/credentials/${credentialId}/verify`, data);
        return response.data;
    },

    // ========================================================================
    // DOCUMENT TYPES (for dropdowns)
    // ========================================================================

    documentTypes: [
        { value: 'CV', label: 'CV / Resume', required: true },
        { value: 'JOB_DESCRIPTION', label: 'Job Description', required: true },
        { value: 'CONFIDENTIALITY_AGREEMENT', label: 'Confidentiality Agreement', required: true },
        { value: 'CONTRACT_SOW', label: 'Contract / Statement of Work', required: false },
        { value: 'GCP_CERTIFICATE', label: 'GCP Certificate', required: false },
        { value: 'PROFESSIONAL_LICENSE', label: 'Professional License', required: false },
        { value: 'SIGNATURE_SPECIMEN', label: 'Signature Specimen', required: false },
        { value: 'CODE_OF_CONDUCT', label: 'Code of Conduct', required: false },
        { value: 'ANNUAL_ATTESTATION', label: 'Annual Attestation', required: false },
        { value: 'OTHER', label: 'Other Document', required: false }
    ],

    credentialTypes: [
        { value: 'GCP_CERTIFICATE', label: 'GCP Certificate' },
        { value: 'MEDICAL_LICENSE', label: 'Medical License (MD/DO)' },
        { value: 'NURSING_LICENSE', label: 'Nursing License (RN/LPN)' },
        { value: 'PHARMACY_LICENSE', label: 'Pharmacy License (PharmD)' },
        { value: 'PROFESSIONAL_CERTIFICATION', label: 'Professional Certification' },
        { value: 'DEGREE', label: 'Academic Degree' },
        { value: 'SPECIALTY_CERTIFICATION', label: 'Specialty Certification' },
        { value: 'OTHER', label: 'Other Credential' }
    ],

    employmentTypes: [
        { value: 'employee', label: 'Employee' },
        { value: 'contractor', label: 'Contractor' },
        { value: 'consultant', label: 'Consultant' },
        { value: 'external', label: 'External' }
    ],

    employmentStatuses: [
        { value: 'active', label: 'Active' },
        { value: 'on_leave', label: 'On Leave' },
        { value: 'terminated', label: 'Terminated' },
        { value: 'pending', label: 'Pending' }
    ]
};

export default personnelApi;
